import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LoadingButton } from '@mui/lab';
import { Header } from 'components/Header';
import ServiceDetailHighlights, {
  highlightedLabelsForSelection,
} from '../components/ServiceDetailHighlights';
import { SelectedService, useServiceConfigs } from '../context/ServiceConfigContext';
import { ServiceConfig } from '../services/types';
import { UpdateCartRequest, updateCart } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { DEFAULT_SERVICE_ICON, getServiceIconAsset } from '../utils/serviceIcons';
import { getServicePricing } from '../utils/servicePricing';

interface SummaryPageProps {
  externalIdentity: string;
  cartId: string;
  productId: string;
  storeId: string;
}

const toAttributeKey = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const SummaryPage: React.FC<SummaryPageProps> = ({
  externalIdentity,
  cartId,
  productId: propProductId,
  storeId: propStoreId,
}) => {
  const navigate = useNavigate();
  const {
    configs,
    selectedServices,
    setSelectedServices,
    customer,
    productId: contextProductId,
    storeId: contextStoreId,
    associateId: contextAssociateId,
    associateEmail: contextAssociateEmail,
    associateName: contextAssociateName,
  } = useServiceConfigs();
  const productId = contextProductId ?? propProductId;
  const storeId = contextStoreId ?? propStoreId;
  const resolvedAssociateId = contextAssociateId ?? null;
  const resolvedAssociateEmail = contextAssociateEmail ?? null;
  const resolvedAssociateName = contextAssociateName ?? resolvedAssociateEmail ?? resolvedAssociateId ?? null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addedConfigs = useMemo(
    () =>
      selectedServices
        .map((selection) => {
          const cfg = configs?.find((c) => c.id === selection.id);
          return cfg ? { config: cfg, selection } : null;
        })
        .filter((item): item is { config: ServiceConfig; selection: SelectedService } => !!item),
    [configs, selectedServices]
  );

  const totalCost = useMemo(
    () =>
      addedConfigs.reduce((sum, { config, selection }) => {
        const unitCost = Number(config.cost || 0);
        const quantity = Math.max(1, selection.quantity || 1);
        return sum + unitCost * quantity;
      }, 0),
    [addedConfigs]
  );

  const removeService = (lineId: string) => {
    setSelectedServices(selectedServices.filter((s) => s.lineId !== lineId));
  };

  const canSubmit = addedConfigs.length > 0;

  const formatLocation = (location?: string) => {
    if (!location) return '—';
    return location
      .replace(/Kerrville/g, 'Product Care')
      .replace(/Store/g, 'In Store');
  };

  const handleConfirm = async () => {
    if (!canSubmit) {
      setSnack('Please add a service before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const parentUuid = uuidv4().toString();

      const cartAddOns = addedConfigs.flatMap(({ config, selection }) => {
        const baseAttributes = [
          { key: 'parent_product_id', value: productId },
          { key: 'location_type', value: selection.location },
          { key: 'parent_unique_id', value: parentUuid },
          { key: 'service_description', value: selection.description },
          ...(selection.serviceNote ? [{ key: 'service_note', value: selection.serviceNote }] : []),
        ];

        if (selection.requiredInfoValues) {
          Object.entries(selection.requiredInfoValues).forEach(([label, value]) => {
            if (value == null || value === '') return;
            baseAttributes.push({ key: toAttributeKey(label), value });
          });
        }

        const pricing = getServicePricing(selection, config);
        const createAttributes = () => baseAttributes.map((attr) => ({ ...attr }));

        if (pricing.isEngraving) {
          return [
            {
              productId: selection.sku,
              priceSource: 'internal',
              priceSourceId: pricing.priceSourceId,
              attributes: createAttributes(),
            },
          ];
        }

        const quantity = Math.max(1, selection.quantity || 1);
        return Array.from({ length: quantity }, () => ({
          productId: selection.sku,
          priceSource: 'internal',
          priceSourceId: pricing.priceSourceId,
          attributes: createAttributes(),
        }));
      });

      const updateCartRequest: UpdateCartRequest = {
        productId,
        priceSource: 'internal',
        priceSourceId: 'default',
        attributes: [
          { key: 'integration', value: 'SERVICE_REQUEST' },
          { key: 'unique_id', value: parentUuid },
          { key: 'customer_id', value: customer!.id! },
          { key: 'customer_name', value: `${customer!.firstName} ${customer!.lastName}` },
          { key: 'customer_email', value: customer!.email! },
          { key: 'service_type', value: 'SPECIAL_ORDER' },
          { key: 'item_location', value: storeId },
          { key: 'item_location_type', value: 'Store' },
          ...(resolvedAssociateId ? [{ key: 'associate_id', value: resolvedAssociateId }] : []),
          ...(resolvedAssociateEmail ? [{ key: 'associate_email', value: resolvedAssociateEmail }] : []),
          ...(resolvedAssociateName ? [{ key: 'associate_name', value: resolvedAssociateName }] : []),
        ],
        cartAddOns,
      };

      await updateCart(externalIdentity, cartId, updateCartRequest);

      window.open(`com.newstore.associate-one://cart/load?cartId=${cartId}`);
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'close' }));
    } catch (err) {
      logApiError('Confirming cart update', err);
      const message = formatApiErrorMessage(err, 'Submission failed. Please try again.');
      setError(message);
      setSnack(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Selected Services</Typography>}
        subtitle={addedConfigs.length ? `${addedConfigs.length} item${addedConfigs.length === 1 ? '' : 's'}` : undefined}
        showBackButton
        onBack={() => navigate(-1)}
        align="left"
        sticky
        elevateOnScroll
        serviceCount={selectedServices.length}
      />

      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
          background:
            theme.palette.mode === 'light'
              ? `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.18)} 0%, ${alpha(
                theme.palette.background.paper,
                0.9
              )} 100%)`
              : alpha(theme.palette.primary.dark, 0.25),
        })}
      >
        <CardContent sx={{ py: 2.25 }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                icon={<AttachMoneyOutlinedIcon />}
                label={`Total: $${totalCost.toFixed(2)}`}
                color="primary"
                variant="filled"
                sx={{ borderRadius: 2, fontWeight: 700 }}
              />
              <Typography variant="caption" color="text.secondary">
                Review your selected services below.
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack spacing={1.5} sx={{ mx: 2, mt: 2 }}>
        {addedConfigs.length ? (
          addedConfigs.map(({ config, selection }) => {
            const quantity = Math.max(1, selection.quantity || 1);
            const unitCost = Number(config.cost || 0);
            const totalPrice = unitCost * quantity;
            const iconSrc = getServiceIconAsset(config.icon);
            const requiredFields = new Map(
              (config.requiredInfo ?? []).map((field) => [field.label, field])
            );
            const highlightLabels = highlightedLabelsForSelection(selection);
            const infoChips = Object.entries(selection.requiredInfoValues ?? {})
              .map(([label, value]) => {
                if (!value) return null;
                if (highlightLabels.has(label)) return null;
                const field = requiredFields.get(label);
                let displayValue = value;
                if (field?.valueType === 'Boolean') {
                  displayValue = value === 'true' ? 'Yes' : value === 'false' ? 'No' : value;
                }
                return (
                  <Chip
                    key={`${selection.lineId}-${label}`}
                    size="small"
                    variant="outlined"
                    label={`${label}: ${displayValue}`}
                    sx={{ borderRadius: 1.5 }}
                  />
                );
              })
              .filter(Boolean);

            return (
              <Paper
                key={selection.lineId}
                variant="outlined"
                sx={(theme) => ({
                  position: 'relative',
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.divider, 0.9),
                  display: 'flex',
                  gap: 1.25,
                  alignItems: 'center',
                  transition: 'box-shadow .18s ease',
                  '&:hover': { boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.08)}` },
                })}
              >
                <Box
                  sx={(theme) => ({
                    minWidth: 38,
                    minHeight: 38,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    overflow: 'hidden',
                  })}
                  aria-hidden
                >
                  <Box
                    component="img"
                    src={iconSrc}
                    alt={`${config.description ?? 'Service'} icon`}
                    sx={{ width: 24, height: 24 }}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = DEFAULT_SERVICE_ICON;
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap title={config.description}>
                      {config.description}
                    </Typography>
                    <Chip
                      size="small"
                      icon={<AttachMoneyOutlinedIcon />}
                      label={`$${totalPrice.toFixed(2)}`}
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: 1.5 }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<PlaceOutlinedIcon sx={{ fontSize: 18 }} />}
                      label={formatLocation(selection.location)}
                      sx={{ borderRadius: 1.5 }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Qty: ${quantity}`}
                      sx={{ borderRadius: 1.5 }}
                    />
                    {infoChips}
                  </Stack>
                  <ServiceDetailHighlights selection={selection} requiredFields={requiredFields} />
                  {selection.serviceNote && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Note: {selection.serviceNote}
                    </Typography>
                  )}
                </Box>

                <Tooltip title="Remove service">
                  <IconButton
                    aria-label="Remove service"
                    onClick={() => removeService(selection.lineId)}
                    size="small"
                    sx={(theme) => ({
                      backgroundColor: alpha(theme.palette.error.main, 0.06),
                      color: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.12),
                        color: darken(theme.palette.error.main, 0.1),
                      },
                    })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            );
          })
        ) : (
          <Alert
            severity="info"
            variant="outlined"
            sx={(theme) => ({
              mt: 1,
              borderRadius: 2,
              borderColor: alpha(theme.palette.info.main, 0.5),
            })}
          >
            <AlertTitle>No services added</AlertTitle>
            You haven’t added any services yet.
          </Alert>
        )}
      </Stack>

      {/* Actions */}
      <Box mt={3} mx={2} mb={4}>
        <Stack spacing={1.25}>
          <LoadingButton
            variant="contained"
            fullWidth
            color="primary"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={!canSubmit || isSubmitting}
            startIcon={<CheckCircleIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Submit
          </LoadingButton>
        </Stack>
      </Box>

      <Snackbar open={!!snack} autoHideDuration={2200} onClose={() => setSnack(null)} message={snack ?? ''} />

      {error && (
        <Box mx={2} mb={4}>
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        </Box>
      )}

      <Backdrop
        open={isSubmitting}
        sx={(theme) => ({ color: theme.palette.common.white, zIndex: theme.zIndex.modal + 1 })}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SummaryPage;
